import { ORDER_ITEM_TYPE, ProductIds } from '@mixtiles/web-backend-shared'
import { analytics } from './Analytics'
import { convertPriceToUSDCurrency } from '../../utils/utils'
import { promoCodeManager } from '../PromoCodeManager'
import PaymentManager from '../PaymentManager'
import UserManager from '../UserManager'
import { generateStylingProperties } from './photoAnalytics/utils'
import {
  getBookPriceTier,
  getPhotoBookProductProps,
  getOrderCoverProps,
} from './photoBooksAnalytics'
import { getEnrichedFacebookEventData } from './facebookAnalytics'
import StorageManager from '../StorageManager'
import { WHO_IS_IT_FOR_KEY } from '../../pages/EmailCapturePage/EmailCapturePage.consts'
import { PRODUCT_TYPES } from '../ProductTypeState'
import { getAddressStore } from '../../stores/RootStoreProvider'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'

function getSpecificProductFields(productType, items) {
  if (productType === PRODUCT_TYPES.PHOTO_BOOK) {
    return {}
  }

  const photoWallItems = items.filter(
    (i) => i.itemType === ORDER_ITEM_TYPE.PHOTO_WALL
  )
  const singleTilesItems = items.filter(
    (i) => i.itemType === ORDER_ITEM_TYPE.SINGLE_TILES
  )
  const tilesCount = items.map((i) => i.orderTiles).flat().length
  return {
    'Photo Wall Items Count': photoWallItems.length,
    'Photo Wall Items - Product IDs': photoWallItems.map((i) => i.itemId),
    'Single Tiles Items Count': singleTilesItems.length,
    'Single Tiles Items - Product IDs': singleTilesItems.map((i) => i.itemId),
    'Tiles Count': tilesCount,
  }
}

export function generateCartAnalytics(
  items,
  pricingSummary,
  withPricingSummaryFields = true
) {
  const pricingSummaryFields = withPricingSummaryFields
    ? {
        'Cart Value in USD - Original': convertPriceToUSDCurrency(
          pricingSummary.priceBeforeTax + pricingSummary.discountPrice,
          pricingSummary.currency
        ),
        'Cart Value in USD - Actual': convertPriceToUSDCurrency(
          pricingSummary.priceBeforeTax,
          pricingSummary.currency
        ),
        'Current Volume Discount': pricingSummary?.quantityDiscount?.discount,
        'Next Volume Discount': pricingSummary?.quantityDiscount?.nextDiscount,
        'Discount ID': pricingSummary?.discountId,
      }
    : null

  const addressExists = readOutsideReaction(
    () => !!getAddressStore().currentAddress
  )
  return {
    ...pricingSummaryFields,
    ...getSpecificProductFields(pricingSummary?.productType, items),
    'Cart Items Count': pricingSummary?.products?.length || items.length,
    'Cart Items - Product IDs': items.map((i) => i.itemId),
    'Coupon Code': promoCodeManager.getDiscountCoupon()?.code,
    'Address Exists': addressExists,
    'Payment Method Exists': !!PaymentManager.getSelectedPaymentMethodType(),
  }
}

export function generateCartItemAnalytics(item, isPriceTierEnabled) {
  const {
    itemId,
    itemType,
    title,
    totalPrice,
    orderTiles = [],
    metadata,
  } = item

  const itemPricingProps = {
    'Total Price in USD - Original': convertPriceToUSDCurrency(
      totalPrice?.value,
      totalPrice?.currency
    ),
  }

  if (itemType === ORDER_ITEM_TYPE.PHOTO_BOOK) {
    // The order's serialized metadata: one flat entry per printed page side.
    const photosCount = metadata.pages.flatMap((page) => page.photos).length
    return {
      ...getPhotoBookProductProps(metadata?.bookSize),
      ...getOrderCoverProps(metadata?.cover),
      'Uploaded Photos Count': photosCount,
      ...(isPriceTierEnabled && {
        'Price Tier': getBookPriceTier(metadata.bookSize, photosCount),
      }),
      ...itemPricingProps,
    }
  }

  ProductIds.GIFT_CARD_VALUE
  const styling =
    itemId !== ProductIds.GIFT_CARD_VALUE
      ? generateStylingProperties(item) || {}
      : {}
  return {
    'Product ID': itemId,
    'Product Name': title,
    'Category Name': metadata?.categoryName,
    'Tiles Count': orderTiles.length,
    'Product Type': itemType,
    ...itemPricingProps,
    ...styling,
  }
}

export function trackAddToCart({
  orderTrackingData,
  orderItems,
  value,
  currency,
  productType,
  contentParams = {},
}) {
  UserManager.getInitialUserParams().then((initialUserParams) => {
    const facebookTrackFunction = orderItems
      ? analytics.facebookTrack
      : analytics.facebookPixelTrackOnceInSession
    const eventData = getEnrichedFacebookEventData({
      orderTrackingData: {
        ...orderTrackingData,
        ...promoCodeManager.getPromoCodeAnalyticsData(),
        'Who Is It For': StorageManager.get(WHO_IS_IT_FOR_KEY) || null,
      },
      initialUserParams,
      orderItems,
      value,
      currency,
      contentParams,
    })
    eventData.product_type = productType
    facebookTrackFunction('AddToCart', eventData)
  })
}
